import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Skeleton,
  Alert,
  Button,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  Avatar,
  Link
} from '@mui/material';
import {
  Description,
  Refresh,
  Phone,
  LocationOn,
  Person,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  Receipt,
  Visibility,
  Download
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useParams } from 'react-router-dom';

// Medstown theme
const medstownTheme = createTheme({
  palette: {
    primary: {
      main: '#1d5244',
      light: '#4a7c59',
      dark: '#0f2920',
    },
    secondary: {
      main: '#2e7d32',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: '#f8f9fa',
          fontWeight: 600,
          color: '#374151',
        },
      },
    },
  },
});

// Loading Skeleton Component
const LoadingSkeleton = () => (
  <TableContainer component={Paper}>
    <Table>
      <TableHead>
        <TableRow>
          {['Order ID', 'Customer', 'Phone', 'Address', 'Status', 'Date', 'Prescription'].map((header) => (
            <TableCell key={header}>
              <Skeleton variant="text" width={80} height={24} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {[...Array(6)].map((_, index) => (
          <TableRow key={index}>
            <TableCell>
              <Skeleton variant="text" width={100} height={20} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={120} height={20} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={100} height={20} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={150} height={20} />
            </TableCell>
            <TableCell>
              <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width={120} height={20} />
            </TableCell>
            <TableCell>
              <Skeleton variant="rectangular" width={60} height={32} sx={{ borderRadius: 1 }} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// Mobile Card Component for responsive design
const MobileCard = ({ order }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'pending': return <HourglassEmpty sx={{ fontSize: 16 }} />;
      case 'cancelled': return <Cancel sx={{ fontSize: 16 }} />;
      default: return <Receipt sx={{ fontSize: 16 }} />;
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {order.orderId}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {order.order_ref_id && `Ref: ${order.order_ref_id}`}
          </Typography>
        </Box>
        <Chip
          icon={getStatusIcon(order.orderStatus)}
          label={order.orderStatus}
          color={getStatusColor(order.orderStatus)}
          size="small"
          sx={{ fontWeight: 500 }}
        />
      </Box>

      <Box mb={2}>
        <Box display="flex" alignItems="center" mb={1}>
          <Person sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {order.customerName}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" mb={1}>
          <Phone sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {order.customerPhone}
          </Typography>
        </Box>
        <Box display="flex" alignItems="flex-start">
          <LocationOn sx={{ fontSize: 16, mr: 1, color: 'text.secondary', mt: 0.2 }} />
          <Typography variant="body2" color="text.secondary">
            {order.customer_address.city}, {order.customer_address.district}, {order.customer_address.region} - {order.customer_address.postalCode}
          </Typography>
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {new Date(order.createdAt).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </Typography>
        {order.precriptionUrl && (
          <Button
            size="small"
            startIcon={<Visibility />}
            onClick={() => window.open(order.precriptionUrl, '_blank')}
            sx={{ minWidth: 'auto' }}
          >
            View Rx
          </Button>
        )}
      </Box>

      {order.notes && (
        <Box mt={2} p={1} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="body2" color="text.secondary">
            <strong>Notes:</strong> {order.notes}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default function PPOrders({userId}) {
//   const { user_id } = useParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const fetchPPOrders = async () => {
  setLoading(true);
  setError(null);

  try {
    const baseUrl = "https://api.medstown.com";
    const targetUserId = userId;
    const response = await fetch(
      `${baseUrl}/api/admin-controls/user/pp-orders/${targetUserId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const result = await response.json(); // ✅ Always parse JSON

    if (!response.ok) {
      // show API message if available
      throw new Error(result.message || `HTTP error! status: ${response.status}`);
    }

    if (result.success && result.data) {
      // Sort by creation date in descending order (newest first)
      const sortedList = (result.data.list || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setOrders(sortedList);
      setTotalCount(result.data.totalCount || 0);
    } else {
      throw new Error(result.message || "Failed to fetch PP orders");
    }
  } catch (err) {
    console.error("Error fetching PP orders:", err);
    setError(err.message || "An error occurred while fetching prescription orders");
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    if (userId) {
      fetchPPOrders();
    }
  }, [userId]);

  const handleRetry = () => {
    fetchPPOrders();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'pending': return <HourglassEmpty sx={{ fontSize: 16 }} />;
      case 'cancelled': return <Cancel sx={{ fontSize: 16 }} />;
      default: return <Receipt sx={{ fontSize: 16 }} />;
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={medstownTheme}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <Description sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Skeleton variant="text" width={250} height={32} />
                  <Skeleton variant="text" width={150} height={20} />
                </Box>
              </Box>
            </Box>
          </Paper>
          <LoadingSkeleton />
        </Container>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={medstownTheme}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Alert 
            severity="error"
            sx={{ mb: 3, borderRadius: 2 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleRetry}
                startIcon={<Refresh />}
              >
                Retry
              </Button>
            }
          >
            <Typography variant="h6" gutterBottom>{error}</Typography>
            
          </Alert>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={medstownTheme}>
      <Container maxWidth="xl" >
        {/* Header */}
        {/* <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.main', color: 'white' }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center">
              <Description sx={{ mr: 2, fontSize: 32 }} />
              <Box>
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700 }}>
                  Prescription Orders
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {totalCount} total orders found
                </Typography>
              </Box>
            </Box>
            <Tooltip title="Refresh Data">
              <IconButton onClick={handleRetry} sx={{ color: 'white' }}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper> */}

        {orders.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>No prescription orders found</Typography>
            This user hasn't placed any prescription orders yet.
          </Alert>
        ) : (
          <>
            {/* Mobile View */}
            {isMobile ? (
              <Box>
                {orders.map((order) => (
                  <MobileCard key={order._id} order={order} />
                ))}
              </Box>
            ) : (
              /* Desktop Table View */
              <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Receipt sx={{ mr: 1, fontSize: 18 }} />
                          Order ID
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Person sx={{ mr: 1, fontSize: 18 }} />
                          Customer
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Phone sx={{ mr: 1, fontSize: 18 }} />
                          Phone
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <LocationOn sx={{ mr: 1, fontSize: 18 }} />
                          Address
                        </Box>
                      </TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Order Date</TableCell>
                      <TableCell>Prescription</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order) => (
                      <TableRow key={order._id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                              {order.orderId}
                            </Typography>
                            {order.order_ref_id && (
                              <Typography variant="body2" color="text.secondary">
                                Ref: {order.order_ref_id}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {order.customerName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {order.customerPhone}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                            {order.customer_address.city}, {order.customer_address.district}
                            <br />
                            {order.customer_address.region} - {order.customer_address.postalCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(order.orderStatus)}
                            label={order.orderStatus}
                            color={getStatusColor(order.orderStatus)}
                            size="small"
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(order.createdAt).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {order.precriptionUrl ? (
                            <Tooltip title="View Prescription">
                              <IconButton
                                size="small"
                                onClick={() => window.open(order.precriptionUrl, '_blank')}
                                color="primary"
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Container>
    </ThemeProvider>
  );
}
